<template>
	<div class="regulatory-assistant-panel" :class="{ locked }">
		<div ref="scrollerEl" class="messages" :inert="locked">
			<template v-if="messages.length === 0 && !isBusy">
				<div v-if="parcelId" class="intro">
					<AppIcon :name="IconName.MessageSquare" :size="16" />
					<p>{{ $t("regulatoryAssistant.intro") }}</p>
				</div>
				<div v-else class="intro general">
					<AppIcon :name="IconName.MessageSquare" :size="16" />
					<div>
						<p>{{ $t("regulatoryAssistant.generalIntro.availability") }}</p>
						<p>{{ $t("regulatoryAssistant.generalIntro.topicsLabel") }}</p>
						<ul>
							<li>{{ $t("regulatoryAssistant.generalIntro.topics.parcel") }}</li>
							<li>{{ $t("regulatoryAssistant.generalIntro.topics.zone") }}</li>
							<li>{{ $t("regulatoryAssistant.generalIntro.topics.regulations") }}</li>
						</ul>
					</div>
				</div>
				<div class="suggested-prompts">
					<button
						v-for="prompt in suggestedPrompts"
						:key="prompt"
						type="button"
						class="suggested-prompt"
						@click="ask(prompt)"
					>
						{{ prompt }}
					</button>
				</div>
			</template>

			<template v-else>
				<div class="history-actions">
					<Button type="button" variant="ghost" size="xs" class="clear-button" :disabled="isBusy" @click="clear">
						<AppIcon :name="IconName.TrashSmall" :size="14" />
						{{ $t("regulatoryAssistant.clear") }}
					</Button>
				</div>

				<article
					v-for="message in messages"
					:key="message.id"
					:data-message-id="message.id"
					class="message"
					:class="message.kind"
				>
					<p v-if="message.kind === RegulatoryMessageKind.Question">{{ message.question }}</p>

					<RegulatoryAssistantAnswer
						v-else-if="message.kind === RegulatoryMessageKind.Answer"
						:response="message.response"
						:duration-ms="message.durationMs"
					/>

					<template v-else>
						<div class="error-content">
							<AppIcon :name="IconName.CircleAlert" :size="16" />
							<p>{{ $t(`regulatoryAssistant.failures.${message.failure}`) }}</p>
						</div>
						<Button
							v-if="message.id === messages.at(-1)?.id"
							type="button"
							variant="ghost"
							size="xs"
							class="retry-button"
							:disabled="isBusy"
							@click="retry(message.id)"
						>
							<AppIcon :name="IconName.RotateCcw" :size="14" />
							{{ $t("regulatoryAssistant.retry") }}
						</Button>
					</template>
				</article>

				<div v-if="isBusy" class="message loading">
					<div class="loading-header">
						<span class="loader"></span>
						<span>{{ $t("regulatoryAssistant.loading") }}</span>
						<span class="elapsed">{{ formatDuration(elapsedMs) }}</span>
					</div>
					<div class="skeleton"></div>
					<div class="skeleton"></div>
					<div class="skeleton short"></div>
				</div>
			</template>
		</div>

		<form class="composer" :inert="locked" @submit.prevent="submit">
			<textarea
				ref="composerEl"
				v-model="question"
				:placeholder="$t('regulatoryAssistant.placeholder')"
				:aria-label="$t('regulatoryAssistant.inputLabel')"
				:disabled="isBusy"
				rows="1"
				@keydown="onComposerKeydown"
			/>
			<Button
				v-if="isBusy"
				type="button"
				variant="danger"
				size="s"
				class="composer-button"
				:title="$t('regulatoryAssistant.stop')"
				:aria-label="$t('regulatoryAssistant.stop')"
				@click="stop"
			>
				<AppIcon :name="IconName.Stop" :size="16" />
			</Button>
			<Button
				v-else
				type="submit"
				variant="primary"
				size="s"
				class="composer-button"
				:title="$t('regulatoryAssistant.send')"
				:aria-label="$t('regulatoryAssistant.send')"
				:disabled="!question.trim()"
			>
				<AppIcon :name="IconName.Send" :size="16" />
			</Button>
		</form>

		<div v-if="locked" class="locked-overlay">
			<p>{{ $t("extendedAccess.assistantCta.message") }}</p>
			<Button type="button" variant="primary" size="m" @click="emit('unlock')">
				<AppIcon :name="IconName.Lock" :size="16" :stroke-width="1.75" />
				{{ $t("extendedAccess.assistantCta.action") }}
			</Button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useNow, useTextareaAutosize } from "@vueuse/core"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import Button from "@/components/ui/Button.vue"
import RegulatoryAssistantAnswer from "@/components/parcels/assistant/RegulatoryAssistantAnswer.vue"
import { useRegulatoryAssistantStore } from "@/stores/regulatoryAssistantStore"
import { RegulatoryMessageKind } from "@/types/regulatoryAssistant"
import { formatDuration } from "@/utils/format"

const props = defineProps<{
	parcelId?: string | null
	zoneCode?: string | null
	locked?: boolean
}>()

const emit = defineEmits<{
	unlock: []
}>()

const conversationParcelId = computed(() => props.parcelId ?? null)

const { t } = useI18n()
const assistantStore = useRegulatoryAssistantStore()
const { now, pause: pauseClock, resume: resumeClock } = useNow({ interval: 100, controls: true })

const question = ref("")
const scrollerEl = ref<HTMLElement | null>(null)
const composerEl = ref<HTMLTextAreaElement>()

useTextareaAutosize({ element: composerEl, input: question })

const messages = computed(() => assistantStore.messages(conversationParcelId.value))
const isBusy = computed(() => assistantStore.isBusy(conversationParcelId.value))
const elapsedMs = computed(() => {
	const startedAt = assistantStore.requestStartedAt(conversationParcelId.value)
	return startedAt ? now.value.getTime() - startedAt : 0
})
const suggestedPrompts = computed(() => {
	if (!props.parcelId) {
		return [
			t("regulatoryAssistant.suggestedPrompts.urbanPlan"),
			t("regulatoryAssistant.suggestedPrompts.planningPolicies"),
		]
	}
	return [
		t("regulatoryAssistant.suggestedPrompts.developmentPotential", { parcelId: props.parcelId }),
		...(props.zoneCode ? [t("regulatoryAssistant.suggestedPrompts.zoneUses", { zoneCode: props.zoneCode })] : []),
		t("regulatoryAssistant.suggestedPrompts.constraints"),
	]
})

function ask(text: string): void {
	assistantStore.ask(conversationParcelId.value, text)
}

function submit(): void {
	if (isBusy.value || !question.value.trim()) return
	ask(question.value)
	question.value = ""
}

function retry(messageId: string): void {
	assistantStore.retry(conversationParcelId.value, messageId)
}

function stop(): void {
	assistantStore.stop(conversationParcelId.value)
}

function clear(): void {
	assistantStore.clear(conversationParcelId.value)
	nextTick(() => composerEl.value?.focus())
}

function onComposerKeydown(event: KeyboardEvent): void {
	if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
		event.preventDefault()
		submit()
	}
}

function scrollToLatest(): void {
	nextTick(() => {
		const scroller = scrollerEl.value
		const lastMessage = messages.value.at(-1)
		if (!scroller) return

		const isAnswerReceived = !isBusy.value && lastMessage && lastMessage.kind !== RegulatoryMessageKind.Question
		const lastMessageEl = isAnswerReceived
			? scroller.querySelector<HTMLElement>(`[data-message-id="${lastMessage.id}"]`)
			: null
		const top = lastMessageEl ? lastMessageEl.offsetTop : scroller.scrollHeight
		scroller.scrollTo({ top, behavior: "smooth" })
	})
}

watch(() => [messages.value.length, isBusy.value], scrollToLatest)

watch(isBusy, busy => (busy ? resumeClock() : pauseClock()), { immediate: true })

watch(
	() => props.parcelId,
	() => {
		question.value = ""
	}
)
</script>

<style scoped>
.regulatory-assistant-panel {
	position: relative;
	display: flex;
	flex: 1;
	flex-direction: column;
	min-height: 0;
	font-family: var(--font-family-primary);

	&.locked {
		.messages,
		.composer {
			filter: blur(3px);
			user-select: none;
		}
	}

	.locked-overlay {
		position: absolute;
		inset: 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-md);
		padding: var(--spacing-lg);
		background: color-mix(in srgb, var(--neutral-0) 50%, transparent);
		text-align: center;

		p {
			margin: 0;
			color: var(--neutral-900);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-medium);
			line-height: var(--line-height-normal);
		}

		.btn {
			width: fit-content;
			gap: var(--spacing-xs);
		}
	}

	.messages {
		position: relative;
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: var(--spacing-md);
		min-height: 0;
		overflow-y: auto;
		padding: var(--spacing-md) var(--spacing-lg);
	}

	.intro {
		display: flex;
		align-items: center;
		gap: var(--spacing-smd);
		padding: var(--spacing-md);
		border-radius: var(--border-radius-base);
		background: var(--neutral-50);
		color: var(--neutral-600);
		font-size: var(--font-size-small);
		line-height: var(--line-height-normal);

		.app-icon {
			flex-shrink: 0;
			color: var(--primary-green);
		}

		&.general {
			align-items: flex-start;

			ul {
				margin: var(--spacing-xs) 0 0;
				padding-left: var(--spacing-md);
			}
		}
	}

	.suggested-prompts {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		margin-top: auto;
	}

	.suggested-prompt {
		padding: var(--spacing-sm) var(--spacing-md);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-base);
		background: var(--neutral-0);
		color: var(--neutral-900);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-small);
		text-align: left;
		cursor: pointer;

		&:hover {
			border-color: var(--primary-green);
		}
	}

	.history-actions {
		display: flex;
		justify-content: flex-end;

		.clear-button {
			gap: var(--spacing-xs);

			.app-icon {
				transform: translateY(calc(-1 * var(--space-2)));
			}
		}
	}

	.message {
		padding: var(--spacing-sm) var(--spacing-md);
		border-radius: var(--border-radius-base);
		font-size: var(--font-size-small);
		line-height: var(--line-height-normal);

		&.question {
			align-self: flex-end;
			border: var(--border-width-thin) solid var(--neutral-200);
			background: var(--neutral-0);
			color: var(--neutral-900);
			white-space: pre-wrap;
		}

		&.answer,
		&.loading {
			align-self: flex-start;
			border: var(--border-width-thin) solid var(--neutral-50);
			background: var(--neutral-50);
		}

		&.error {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			gap: var(--spacing-sm);
			align-self: flex-start;
			padding: 0;
			color: var(--neutral-900);

			.error-content {
				display: flex;
				align-items: flex-start;
				gap: var(--spacing-sm);
				padding: var(--spacing-sm) var(--spacing-md);
				border: var(--border-width-thin) solid color-mix(in srgb, var(--accent-warning) 30%, transparent);
				border-radius: var(--border-radius-base);
				background: color-mix(in srgb, var(--accent-warning) 8%, transparent);

				.app-icon {
					flex-shrink: 0;
					color: var(--accent-warning);
				}
			}

			.retry-button {
				gap: var(--spacing-xs);
				color: var(--neutral-700);

				.app-icon {
					transform: translateY(calc(-1 * var(--space-2)));
				}
			}
		}

		&.loading {
			align-self: stretch;
			display: flex;
			flex-direction: column;
			gap: var(--spacing-sm);
			color: var(--neutral-600);

			.loading-header {
				display: flex;
				align-items: center;
				gap: var(--spacing-sm);
			}

			.elapsed {
				margin-left: auto;
				font-variant-numeric: tabular-nums;
			}

			.loader {
				width: var(--size-icon-md);
				height: var(--size-icon-md);
				border: var(--border-width-base) solid var(--primary-green);
				border-bottom-color: transparent;
				border-radius: var(--border-radius-full);
				animation: rotation 1s linear infinite;
			}

			.skeleton {
				height: var(--spacing-sm);
				border-radius: var(--border-radius-sm);
				background: var(--neutral-200);
				animation: pulse 1.5s ease-in-out infinite;

				&.short {
					width: 70%;
				}
			}
		}
	}

	.composer {
		display: flex;
		flex-shrink: 0;
		align-items: flex-end;
		gap: var(--spacing-sm);
		margin: 0 var(--spacing-lg) var(--spacing-md);

		textarea {
			flex: 1;
			min-width: 0;
			min-height: var(--size-button);
			padding: var(--space-6) var(--spacing-sm);
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-base);
			background: var(--neutral-0);
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			line-height: var(--line-height-normal);
			max-height: 6lh;
			overflow-y: auto;
			resize: none;

			&:focus {
				outline: none;
				border-color: var(--neutral-400);
			}
		}

		.composer-button {
			flex-shrink: 0;
			min-width: 0;
			height: var(--size-button);
			padding: 0 var(--spacing-sm);
		}
	}
}

@keyframes rotation {
	to {
		transform: rotate(360deg);
	}
}

@keyframes pulse {
	50% {
		opacity: 0.5;
	}
}
</style>
